import { ReactNode, useCallback, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { CheckCircle, Email, EventRepeat } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import axios from "../../../api/axios";
import { useApiRequest } from "../../../api/axios";
import CustomDataGrid from "../../../components/ui/customDataGrid/customDataGrid.component";
import EntityReferenceLink from "../../../components/ui/entityReference/entityReferenceLink.component";
import { containsStringFilterOperators } from "../../../components/ui/customDataGrid/filterOperators";
import {
  buildServiceReminderCompleteEndpoint,
  buildServiceReminderSendEndpoint,
  ERROR_SNACKBAR,
  SERVICE_REMINDERS_ENDPOINT,
  SUCCESS_SNACKBAR,
} from "../../../consts";
import { useHasPermission } from "../../../contexts/auth";
import {
  SERVICE_REMINDER_CANCELLED,
  SERVICE_REMINDER_COMPLETED,
  SERVICE_REMINDER_PENDING,
  SERVICE_REMINDER_STATUS_COLOR_MAP,
  type ServiceReminder,
} from "../../../types/serviceReminders";

/** Fires a grid refresh after an action changes a row. */
const REFRESH_EVENT = "serviceReminderUpdated";

export default function ServiceRemindersList() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { executeRequest } = useApiRequest();
  const canView = useHasPermission("reminders.view_reminder");
  const canChange = useHasPermission("reminders.change_reminder");
  const canViewEquipment = useHasPermission("settlement.view_equipment");
  const canViewContracts = useHasPermission("settlement.view_contract");
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewJobs = useHasPermission("service.view_job");
  const [busyId, setBusyId] = useState<number | null>(null);

  const runAction = useCallback(
    async (reminder: ServiceReminder, url: string, successMessage: string) => {
      setBusyId(reminder.id);
      const response = await executeRequest(() => axios.post(url, {}));
      setBusyId(null);

      if (response?.success) {
        enqueueSnackbar(successMessage, SUCCESS_SNACKBAR);
        window.dispatchEvent(new Event(REFRESH_EVENT));
        return;
      }

      enqueueSnackbar(
        response?.errorData?.detail ||
          response?.errorData?.message ||
          t("The action could not be completed."),
        ERROR_SNACKBAR
      );
    },
    [enqueueSnackbar, executeRequest, t]
  );

  const renderIdLink = (
    entityType: "client" | "contract" | "job",
    entityId: number | null,
    label: string | null,
    canPreview: boolean
  ) =>
    entityId ? (
      <EntityReferenceLink
        entityType={entityType}
        entityId={entityId}
        label={label}
        canPreview={canPreview}
      />
    ) : (
      label || "—"
    );

  const renderActionButton = (
    title: string,
    icon: ReactNode,
    color: "primary" | "success",
    disabled: boolean,
    onClick: () => void
  ) => (
    <Tooltip title={title}>
      {/* A disabled button fires no events, so the tooltip needs a wrapper. */}
      <span>
        <IconButton
          size="small"
          color={color}
          aria-label={title}
          disabled={disabled}
          onClick={onClick}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  const columns: GridColDef[] = [
    {
      field: "equipments",
      headerName: t("Equipment"),
      flex: 1,
      minWidth: 220,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row as ServiceReminder;
        if (!row.equipments?.length) return "—";
        return (
          <Stack spacing={0.25} sx={{ justifyContent: "center", height: "100%", py: 0.5 }}>
            {row.equipments.map((equipment) => (
              <Tooltip
                key={equipment.id}
                title={
                  equipment.warranty_until
                    ? `${t("Warranty until")}: ${equipment.warranty_until}`
                    : t("No warranty recorded")
                }
              >
                <Box component="span">
                  <EntityReferenceLink
                    entityType="equipment"
                    entityId={equipment.id}
                    label={equipment.full_name}
                    canPreview={canViewEquipment}
                  />
                </Box>
              </Tooltip>
            ))}
          </Stack>
        );
      },
    },
    {
      field: "due_date",
      headerName: t("Service due"),
      width: 170,
      renderCell: (params) => {
        const row = params.row as ServiceReminder;
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%", gap: 1 }}>
            <Typography variant="body2">
              {row.due_date ? dayjs(row.due_date).format("YYYY-MM-DD") : "—"}
            </Typography>
            {row.is_overdue && (
              <Chip size="small" color="error" label={t("Overdue")} />
            )}
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: t("Status"),
      width: 140,
      renderCell: (params) => {
        const row = params.row as ServiceReminder;
        return (
          <Chip
            size="small"
            label={row.status_display || "—"}
            color={SERVICE_REMINDER_STATUS_COLOR_MAP[row.status] ?? "default"}
          />
        );
      },
    },
    {
      field: "contract_number",
      headerName: t("Contract"),
      width: 160,
      filterOperators: containsStringFilterOperators(),
      renderCell: (params) => {
        const row = params.row as ServiceReminder;
        return renderIdLink("contract", row.contract_id, row.contract_number, canViewContracts);
      },
    },
    {
      field: "client_name",
      headerName: t("Client"),
      flex: 1,
      minWidth: 180,
      filterOperators: containsStringFilterOperators(),
      renderCell: (params) => {
        const row = params.row as ServiceReminder;
        return renderIdLink("client", row.client_id, row.client_name, canViewClients);
      },
    },
    {
      field: "notify_at",
      headerName: t("Client is told on"),
      width: 160,
      valueGetter: (value, row) =>
        row.notify_at ? dayjs(row.notify_at).format("YYYY-MM-DD") : "—",
    },
    {
      field: "job_number",
      headerName: t("Job"),
      width: 140,
      filterable: false,
      renderCell: (params) => {
        const row = params.row as ServiceReminder;
        return renderIdLink("job", row.job, row.job_number, canViewJobs);
      },
    },
    {
      field: "actions",
      headerName: t("Actions"),
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row as ServiceReminder;
        if (!canChange) return null;

        const isBusy = busyId === row.id;
        const isDone =
          row.status === SERVICE_REMINDER_COMPLETED || row.status === SERVICE_REMINDER_CANCELLED;

        return (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", height: "100%" }}>
            {row.status === SERVICE_REMINDER_PENDING &&
              renderActionButton(t("Send now"), <Email fontSize="small" />, "primary", isBusy, () =>
                runAction(row, buildServiceReminderSendEndpoint(row.id), t("Reminder sent to the client."))
              )}
            {!isDone &&
              renderActionButton(
                t("Mark as done"),
                <CheckCircle fontSize="small" />,
                "success",
                isBusy,
                () =>
                  runAction(row, buildServiceReminderCompleteEndpoint(row.id), t("Service marked as done."))
              )}
          </Stack>
        );
      },
    },
  ];

  if (!canView) {
    return null;
  }

  return (
    <CustomDataGrid
      icon={<EventRepeat />}
      title={t("Upcoming services")}
      url={SERVICE_REMINDERS_ENDPOINT}
      gridSettingsKey="SERVICE_REMINDERS"
      columns={columns}
      dataGridProps={{ getRowHeight: () => "auto" }}
      refreshEventName={REFRESH_EVENT}
      initialSortModel={[{ field: "due_date", sort: "asc" }]}
    />
  );
}
